/* Verbatim port of the identity localStorage/sessionStorage reads scattered
 * across service/review/index.html: `author()` (~3659), the feedback-modal
 * pre-fill/save (~3485, ~3489), the run-token read in the "Run new report"
 * modal (~3531), and WS.ensureAuthor/switchAuthor's session-confirmation
 * flag (~3663-3678). Key names are byte-identical: 'fb-feedback-name' and
 * 'fb-runtoken' (localStorage), 'fb-name-ok' (sessionStorage).
 *
 * `fb-runtoken` is read-only here because it is read-only in the original
 * too — every call site is `localStorage.getItem('fb-runtoken') || ''`;
 * nothing in the old app ever calls setItem for it. It's set out-of-band,
 * once, via the browser console — the "wrong run token" modal (~3543)
 * literally says "ask the project owner for it", not "type it in here".
 */

import { favForDomain } from './format.ts';

const FEEDBACK_NAME_KEY = 'fb-feedback-name';
const RUN_TOKEN_KEY = 'fb-runtoken';
const NAME_OK_KEY = 'fb-name-ok';

/* ── Brand-logo identity (CRM register + pinned rail) ──────────────────────
 *
 * Owner's locked frontend contract §5 (docs/build-handoff-locked-contract.md):
 * "Use real verified brand logos. Unknown has a neutral non-letter treatment;
 * never use placeholder initials." So the fallback chain for a CLIENT/BRAND is
 * logoUrl (Wikidata P154 enrich, often a data: URI) → primary-domain favicon
 * (favForDomain — the sanctioned external-favicon pattern) → a NEUTRAL
 * NON-LETTER mark (never a monogram/initials). Pure + exported so the unit
 * suite pins the chain; the render site (views/register.ts) turns the result
 * into the design's `.brand-logo` node. */
export type BrandVisual =
  | { kind: 'logo' | 'favicon'; src: string }
  | { kind: 'neutral' };

export function brandVisual(
  r: { domain?: string | null; logoUrl?: string | null },
): BrandVisual {
  if (r.logoUrl) return { kind: 'logo', src: r.logoUrl };
  if (r.domain) return { kind: 'favicon', src: favForDomain(r.domain, 64) };
  return { kind: 'neutral' };
}

/** Two-letter initials for a PERSON avatar (the rail-foot identity button) —
 * initials are correct for people per the locked contract §5, and match the
 * design's "YM" person avatar. NOT for brand logos (see brandVisual). */
export function personInitials(name: string | null | undefined): string {
  const words = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

export function getFeedbackName(): string {
  return localStorage.getItem(FEEDBACK_NAME_KEY) || '';
}

export function setFeedbackName(name: string): void {
  localStorage.setItem(FEEDBACK_NAME_KEY, name);
}

export function getRunToken(): string {
  return localStorage.getItem(RUN_TOKEN_KEY) || '';
}

/** Once per browser session — a cached name from whoever used this machine
 * last must never silently sign someone else's changes (round-3: every
 * tester's first write was misattributed). */
export function isNameConfirmedThisSession(): boolean {
  return !!sessionStorage.getItem(NAME_OK_KEY);
}

export function confirmNameForSession(): void {
  sessionStorage.setItem(NAME_OK_KEY, '1');
}
